/**
 * Network utilities for handling local development with network access
 */

/**
 * Get the correct URL based on how the app is accessed
 * Automatically replaces localhost with the current IP when accessing from another computer
 */
export function getDynamicUrl(envUrl: string | undefined): string | undefined {
  if (!envUrl) return envUrl;

  // If not in development or URL doesn't contain localhost, use as-is
  if (import.meta.env.PROD || !envUrl.includes('localhost')) {
    return envUrl;
  }

  // Check if window is available (not during SSR or build)
  if (typeof window === 'undefined') {
    return envUrl;
  }

  // In development, check if accessed via IP address
  const currentHost = window.location.hostname;

  // If accessing via localhost, use the env URL as-is
  if (currentHost === 'localhost' || currentHost === '127.0.0.1') {
    return envUrl;
  }

  // If accessing via IP address (e.g., 192.168.x.x), replace localhost with that IP
  const isIPAddress = /^(\d{1,3}\.){3}\d{1,3}$/.test(currentHost);
  if (isIPAddress) {
    // Replace localhost with the current IP address
    return envUrl.replace(/localhost|127\.0\.0\.1/g, currentHost);
  }

  // Default to env URL
  return envUrl;
}

/**
 * Check if we're accessing the app from a different computer on the network
 */
export function isNetworkAccess(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const currentHost = window.location.hostname;
  return !/^(localhost|127\.0\.0\.1)$/.test(currentHost);
}

/**
 * Get the host IP address when accessing from network
 */
export function getNetworkHost(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const currentHost = window.location.hostname;
  const isIPAddress = /^(\d{1,3}\.){3}\d{1,3}$/.test(currentHost);
  return isIPAddress ? currentHost : null;
}
